import { ApiHttpError } from '@/shared/api/client';
import { uploadHealthActions } from '../stores/uploadHealthStore';
import {
  type SharedUploadProgress,
  type SharedUploadSnapshot,
  UPLOAD_SHARED_WORKER_PROTOCOL_VERSION,
  type UploadWorkerClientMessage,
  type UploadWorkerServerMessage,
  isUploadWorkerServerMessage,
} from '../types/uploadSharedWorker';
import type { UploadTask } from '../types/uploads';
import { getUploadOffset } from './submissionService';

interface RuntimeHandlers {
  onSnapshot: (snapshot: SharedUploadSnapshot) => void;
  onProgress: (progressByUploadTaskId: Record<string, SharedUploadProgress>) => void;
}

interface PendingFilesRequest {
  resolve: (files: Map<string, File>) => void;
  timeout: ReturnType<typeof setTimeout>;
}

interface PendingSnapshotRequest {
  resolve: (snapshot: SharedUploadSnapshot | null) => void;
  timeout: ReturnType<typeof setTimeout>;
}

const HEARTBEAT_MS = 10_000;
const REQUEST_TIMEOUT_MS = 5_000;
const tabId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

let worker: SharedWorker | null = null;
let activeUid: string | null = null;
let handlers: RuntimeHandlers | null = null;
let latestSnapshot: SharedUploadSnapshot | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let hasLeadership = false;
let requestSequence = 0;
let trackedHealthCandidates: Array<{ sessionId: string; reportedBytes: number }> = [];
const pendingFileRequests = new Map<string, PendingFilesRequest>();
const pendingSnapshotRequests = new Map<string, PendingSnapshotRequest>();
const OFFSET_PROBE_CONCURRENCY = 4;

type ClientPayload = UploadWorkerClientMessage extends infer Message
  ? Message extends UploadWorkerClientMessage
    ? Omit<Message, 'version' | 'uid' | 'tabId'>
    : never
  : never;

function message(payload: ClientPayload): UploadWorkerClientMessage | null {
  if (!activeUid) return null;
  return {
    ...payload,
    version: UPLOAD_SHARED_WORKER_PROTOCOL_VERSION,
    uid: activeUid,
    tabId,
  } as UploadWorkerClientMessage;
}

function post(payload: ClientPayload): boolean {
  const next = message(payload);
  if (!worker || !next) return false;
  try {
    worker.port.postMessage(next);
    return true;
  } catch {
    return false;
  }
}

async function answerOffsetProbe(requestId: string, sessionIds: string[]): Promise<void> {
  const results: Array<
    | { sessionId: string; status: 'available'; offset: number }
    | { sessionId: string; status: 'expired' | 'inconclusive' }
  > = [];
  let cursor = 0;
  const probe = async () => {
    for (;;) {
      const sessionId = sessionIds[cursor];
      cursor += 1;
      if (!sessionId) return;
      try {
        const offset = await getUploadOffset(sessionId);
        results.push({ sessionId, status: 'available', offset });
      } catch (error) {
        results.push({
          sessionId,
          status:
            error instanceof ApiHttpError && error.status === 410 ? 'expired' : 'inconclusive',
        });
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(OFFSET_PROBE_CONCURRENCY, sessionIds.length) }, () => probe()),
  );
  post({ type: 'probe-results', requestId, results });
}

function receive(event: MessageEvent<unknown>): void {
  if (!isUploadWorkerServerMessage(event.data)) return;
  const response: UploadWorkerServerMessage = event.data;
  if (response.uid !== activeUid) return;
  if (
    response.type === 'ready' ||
    response.type === 'snapshot' ||
    response.type === 'snapshot-response'
  ) {
    latestSnapshot = response.snapshot;
    uploadHealthActions.replace(response.snapshot.healthBySessionId);
    handlers?.onSnapshot(response.snapshot);
  }
  if (response.type === 'snapshot-response') {
    const pending = pendingSnapshotRequests.get(response.requestId);
    if (!pending) return;
    clearTimeout(pending.timeout);
    pendingSnapshotRequests.delete(response.requestId);
    pending.resolve(response.snapshot);
    return;
  }
  if (response.type === 'progress') {
    if (latestSnapshot) {
      latestSnapshot = {
        ...latestSnapshot,
        progressByUploadTaskId: response.progressByUploadTaskId,
      };
    }
    handlers?.onProgress(response.progressByUploadTaskId);
  }
  if (response.type === 'health') {
    uploadHealthActions.replace(response.healthBySessionId);
  }
  if (response.type === 'probe-offsets') {
    if (hasLeadership) void answerOffsetProbe(response.requestId, response.sessionIds);
    return;
  }
  if (response.type === 'files-response') {
    const pending = pendingFileRequests.get(response.requestId);
    if (!pending) return;
    clearTimeout(pending.timeout);
    pendingFileRequests.delete(response.requestId);
    pending.resolve(new Map(response.files.map(({ uploadTaskId, file }) => [uploadTaskId, file])));
  } else if (response.type === 'error' && response.requestId) {
    const pending = pendingFileRequests.get(response.requestId);
    if (!pending) return;
    clearTimeout(pending.timeout);
    pendingFileRequests.delete(response.requestId);
    pending.resolve(new Map());
  }
}

function stopHeartbeat(): void {
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  heartbeatTimer = null;
}

function disconnect(): void {
  stopHeartbeat();
  post({ type: 'disconnect' });
  worker?.port.close();
  worker = null;
  hasLeadership = false;
  activeUid = null;
  handlers = null;
  latestSnapshot = null;
  trackedHealthCandidates = [];
  uploadHealthActions.clear();
  for (const pending of pendingFileRequests.values()) {
    clearTimeout(pending.timeout);
    pending.resolve(new Map());
  }
  pendingFileRequests.clear();
  for (const pending of pendingSnapshotRequests.values()) {
    clearTimeout(pending.timeout);
    pending.resolve(null);
  }
  pendingSnapshotRequests.clear();
}

function connect(uid: string, nextHandlers: RuntimeHandlers): boolean {
  if (activeUid === uid && worker) {
    handlers = nextHandlers;
    return true;
  }
  disconnect();
  if (!('SharedWorker' in globalThis)) return false;
  try {
    worker = new SharedWorker(
      new URL('../workers/upload-runtime.shared-worker.ts', import.meta.url),
      {
        name: 'vision-lab-upload-runtime',
        type: 'module',
      },
    );
    activeUid = uid;
    handlers = nextHandlers;
    worker.port.addEventListener('message', receive);
    worker.port.start();
    post({ type: 'connect' });
    if (trackedHealthCandidates.length > 0) {
      post({ type: 'track-health', candidates: trackedHealthCandidates });
    }
    heartbeatTimer = setInterval(() => {
      post({ type: 'heartbeat' });
      if (hasLeadership) post({ type: 'set-leader', leader: true });
    }, HEARTBEAT_MS);
    return true;
  } catch (error) {
    console.warn('[uploads] SharedWorker runtime unavailable; using tab-local recovery', error);
    disconnect();
    return false;
  }
}

function requestFiles(uploadTaskIds: readonly string[]): Promise<Map<string, File>> {
  if (!worker || uploadTaskIds.length === 0) return Promise.resolve(new Map());
  const requestId = `${tabId}:${++requestSequence}`;
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      pendingFileRequests.delete(requestId);
      resolve(new Map());
    }, REQUEST_TIMEOUT_MS);
    pendingFileRequests.set(requestId, { resolve, timeout });
    if (!post({ type: 'request-files', requestId, uploadTaskIds: [...uploadTaskIds] })) {
      clearTimeout(timeout);
      pendingFileRequests.delete(requestId);
      resolve(new Map());
    }
  });
}

function requestSnapshot(): Promise<SharedUploadSnapshot | null> {
  if (!worker) return Promise.resolve(latestSnapshot);
  const requestId = `${tabId}:snapshot:${++requestSequence}`;
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      pendingSnapshotRequests.delete(requestId);
      resolve(latestSnapshot);
    }, REQUEST_TIMEOUT_MS);
    pendingSnapshotRequests.set(requestId, { resolve, timeout });
    if (!post({ type: 'request-snapshot', requestId })) {
      clearTimeout(timeout);
      pendingSnapshotRequests.delete(requestId);
      resolve(latestSnapshot);
    }
  });
}

export const uploadRuntimeClient = {
  connect,
  disconnect,
  isSupported: (): boolean => 'SharedWorker' in globalThis,
  setLeader: (leader: boolean): void => {
    hasLeadership = leader;
    post({ type: 'set-leader', leader });
  },
  publishSnapshot: (items: readonly UploadTask[]): void => {
    post({ type: 'publish-snapshot', items: [...items] });
  },
  publishProgress: (
    progressByUploadTaskId: Readonly<Record<string, SharedUploadProgress>>,
  ): void => {
    post({ type: 'publish-progress', progressByUploadTaskId: { ...progressByUploadTaskId } });
  },
  trackHealth: (candidates: readonly { sessionId: string; reportedBytes: number }[]): void => {
    trackedHealthCandidates = [...candidates];
    post({ type: 'track-health', candidates: trackedHealthCandidates });
  },
  retainFile: (uploadTaskId: string, file: File): void => {
    post({ type: 'retain-file', uploadTaskId, file });
  },
  releaseFile: (uploadTaskId: string): void => {
    post({ type: 'release-file', uploadTaskId });
  },
  requestFiles,
  requestSnapshot,
  latestSnapshot: (): SharedUploadSnapshot | null => latestSnapshot,
};
